// Direct import of selector module needed instead of useBrand hook in selector context where hooks cannot be used.
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {createSelector} from '@reduxjs/toolkit'
import {getHttpRequestId} from 'modules/common/selectors'
import {RootState} from 'store'
import {urlParams} from 'utils/urlParams'

import {getCookie} from '@vio/core/src/_web/utils/cookies'
import {Status as BookingStatus} from '@vio/core/src/api-types/bovio/response/booking_status'
import {
  bookApi,
  CREATE_BOOKING_CACHE_KEY,
  FINALIZE_BOOKING_CACHE_KEY,
  GET_BOOKING_STATUS_CACHE_KEY
} from '@vio/core/src/booking/services/bookApi'
import {isBookingStatusErrorResponse} from '@vio/core/src/booking/utils'
import {
  getIsInvalidEmail,
  getMappedBookingErrorToErrorCategory
} from '@vio/core/src/booking/utils/bookingErrorHelpers'
import {getBovioApiKey} from '@vio/core/src/experiments/modules/selectors'
import {defaultLanguage} from '@vio/core/src/localization/services/locale'

import type {BookingError} from '@vio/core/src/booking/types/ErrorResponse'

const getStateParam = (state: RootState) => state

export const getBookingModuleStatus = (state: RootState): string =>
  state?.book?.offerBookState || ''

const getUrlBookingId = () => urlParams().bookingId || ''
const getUrlRequestId = () => urlParams().requestId
const getBookingLocale = () =>
  urlParams().locale || getCookie('locale') || defaultLanguage

export const getExistingBookingQueryParams = createSelector(
  [
    getHttpRequestId,
    getBovioApiKey,
    getUrlBookingId,
    getUrlRequestId,
    getBookingLocale
  ],
  (httpRequestId, profile, bookingId, requestId, locale) => {
    const boVioEndpoint = process.env.NEXT_PUBLIC_BOFH_ENDPOINT!

    return {
      url: boVioEndpoint,
      bookingId,
      locale,
      profileKey: profile,
      httpRequestId: requestId || httpRequestId,
      clientUserAgent: `checkout/${process.env.NEXT_PUBLIC_APP_VERSION}`
    }
  }
)

export const getCreateBookingResult = createSelector(
  [getStateParam],
  (state: RootState) =>
    bookApi.endpoints.createBooking.select(CREATE_BOOKING_CACHE_KEY)(state)
)
const getBookingFinalizationResult = createSelector(
  [getStateParam],
  (state: RootState) =>
    bookApi.endpoints.bookingFinalization.select(FINALIZE_BOOKING_CACHE_KEY)(
      state
    )
)
const getBookingStatusResult = createSelector(
  [getStateParam],
  (state: RootState) =>
    bookApi.endpoints.getBookingStatus.select(GET_BOOKING_STATUS_CACHE_KEY)(
      state
    )
)

export const getBookingError = createSelector(
  [
    getCreateBookingResult,
    getBookingFinalizationResult,
    getBookingStatusResult
  ],
  (
    {error: createBookingError},
    {error: finalizeBookingError},
    {error: bookingStatusError, data: bookingStatusData}
  ): BookingError | undefined => {
    if (
      isBookingStatusErrorResponse(bookingStatusData) &&
      bookingStatusData?.status === BookingStatus.Error
    ) {
      return {data: bookingStatusData.error}
    }
    const bookingError =
      createBookingError || finalizeBookingError || bookingStatusError

    if (bookingError && 'status' in bookingError) {
      return bookingError
    }
  }
)

export const getIsInvalidEmailBookingError = createSelector(
  [getBookingError],
  error => getIsInvalidEmail(error)
)
export const getBookingErrorType = createSelector([getBookingError], error =>
  getMappedBookingErrorToErrorCategory(error)
)
export const getIsPaymentBookingError = createSelector(
  [getBookingErrorType, getIsInvalidEmailBookingError],
  (bookingErrorType, isInvalidEmailError) =>
    !isInvalidEmailError && bookingErrorType !== undefined
)
